import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { 
  ExclamationTriangleIcon,
  CheckCircleIcon,
  LightBulbIcon,
  ChartBarIcon,
  ClockIcon,
  CurrencyDollarIcon,
  UserGroupIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  MinusIcon,
  EyeIcon,
  CogIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';
import { API_BASE_URL } from '@/config';
import { getAuthHeaders } from '@/api/workosAuth';

interface Insight {
  id: string;
  type: 'critical' | 'positive' | 'recommendation';
  title: string;
  description: string;
  severity: 'high' | 'medium' | 'low';
  category: 'schedule' | 'cost' | 'resource' | 'quality';
  baselineImpact?: string;
  trend: 'improving' | 'declining' | 'stable';
  actionRequired: boolean;
  estimatedImpact?: string;
}

interface Metric {
  name: string;
  current: number;
  baseline: number;
  target: number;
  unit: string;
  trend: 'up' | 'down' | 'stable';
  status: 'good' | 'warning' | 'critical';
}

// Custom hook for insights data
const useInsightsData = (projectId: string | undefined) => {
  const [insights, setInsights] = useState<Insight[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  React.useEffect(() => {
    if (!projectId) {
      setError('Project ID is required');
      setLoading(false);
      return;
    }

    const fetchInsights = async () => {
      try {
        setLoading(true);
        setError(null);
        const headers = await getAuthHeaders();
        const res = await fetch(`${API_BASE_URL}/v1/analytics/projects/${projectId}/insights`, {
          credentials: 'include',
          headers: { ...headers, Accept: 'application/json' },
        });
        if (!res.ok) throw new Error(res.statusText || 'Failed to load insights');
        const data = await res.json();
        const mapped: Insight[] = (Array.isArray(data) ? data : []).map((i: Record<string, unknown>) => ({
          id: String(i.id ?? ''),
          type: (i.type === 'Risk' ? 'critical' : i.type === 'Opportunity' || i.type === 'Performance' ? 'positive' : 'recommendation') as 'critical' | 'positive' | 'recommendation',
          title: String(i.title ?? ''),
          description: String(i.description ?? ''),
          severity: (String(i.priority ?? 'low').toLowerCase() as 'high' | 'medium' | 'low'),
          category: 'schedule' as const,
          baselineImpact: i.impact ? String(i.impact) : undefined,
          trend: 'stable' as const,
          actionRequired: i.priority === 'High',
          estimatedImpact: i.recommendation ? String(i.recommendation) : undefined,
        }));
        setInsights(mapped);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load insights data');
      } finally {
        setLoading(false);
      }
    };

    fetchInsights();
  }, [projectId]);

  return { insights, loading, error };
};

// Custom hook for metrics data
const useMetricsData = (projectId: string | undefined) => {
  const [metrics, setMetrics] = useState<Metric[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  React.useEffect(() => {
    if (!projectId) {
      setError('Project ID is required');
      setLoading(false);
      return;
    }

    const fetchMetrics = async () => {
      try {
        setLoading(true);
        setError(null);
        const headers = await getAuthHeaders();
        const res = await fetch(`${API_BASE_URL}/v1/analytics/projects/${projectId}`, {
          credentials: 'include',
          headers: { ...headers, Accept: 'application/json' },
        });
        if (!res.ok) throw new Error(res.statusText || 'Failed to load metrics');
        const data = await res.json();
        const activities = data?.activities ?? {};
        const progress = data?.progress ?? {};
        const metricsList: Metric[] = [];
        if (activities.overdue != null) {
          metricsList.push({
            name: 'Overdue activities',
            current: activities.overdue,
            baseline: 0,
            target: 0,
            unit: '',
            trend: 'stable',
            status: activities.overdue > 0 ? 'warning' : 'good',
          });
        }
        if (activities.critical != null) {
          metricsList.push({
            name: 'Critical path activities',
            current: activities.critical,
            baseline: 0,
            target: 0,
            unit: '',
            trend: 'stable',
            status: 'good',
          });
        }
        if (progress.completion_rate != null) {
          metricsList.push({
            name: 'Completion rate',
            current: progress.completion_rate,
            baseline: progress.completion_rate,
            target: 100,
            unit: '%',
            trend: 'stable',
            status: progress.completion_rate >= 80 ? 'good' : progress.completion_rate >= 50 ? 'warning' : 'critical',
          });
        }
        setMetrics(metricsList);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load metrics data');
      } finally {
        setLoading(false);
      }
    };

    fetchMetrics();
  }, [projectId]);

  return { metrics, loading, error };
};

const ProjectInsights: React.FC = () => {
  const { projectId } = useParams();
  const [selectedCategory, setSelectedCategory] = useState<'all' | 'schedule' | 'cost' | 'resource' | 'quality'>('all');
  const [showBaselineComparison, setShowBaselineComparison] = useState(true);

  const { insights, loading: insightsLoading, error: insightsError } = useInsightsData(projectId);
  const { metrics, loading: metricsLoading, error: metricsError } = useMetricsData(projectId);

  const getSeverityColor = (severity: string): string => {
    switch (severity) {
      case 'high':
        return 'text-red-600 bg-red-50 border-red-200';
      case 'medium':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'low':
        return 'text-green-600 bg-green-50 border-green-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getTypeColor = (type: string): string => {
    switch (type) {
      case 'critical':
        return 'text-red-600';
      case 'positive':
        return 'text-green-600';
      case 'recommendation':
        return 'text-blue-600';
      default:
        return 'text-gray-600';
    }
  };

  const getTrendIcon = (trend: string): JSX.Element | null => {
    switch (trend) {
      case 'improving':
        return <ArrowTrendingUpIcon className="h-4 w-4 text-green-600" />;
      case 'declining':
        return <ArrowTrendingDownIcon className="h-4 w-4 text-red-600" />;
      case 'stable':
        return <MinusIcon className="h-4 w-4 text-gray-600" />;
      default:
        return null;
    }
  };

  const getMetricStatusColor = (status: string): string => {
    switch (status) {
      case 'good':
        return 'text-green-600';
      case 'warning':
        return 'text-yellow-600';
      case 'critical':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  const filteredInsights = insights.filter(insight => 
    selectedCategory === 'all' || insight.category === selectedCategory
  );

  const criticalIssues = filteredInsights.filter(i => i.type === 'critical');
  const positiveIndicators = filteredInsights.filter(i => i.type === 'positive');
  const recommendations = filteredInsights.filter(i => i.type === 'recommendation');

  if (insightsLoading || metricsLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading project insights...</p>
        </div>
      </div>
    );
  }

  if (insightsError || metricsError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Insights</h3>
          <p className="text-gray-600">{insightsError || metricsError}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Project Insights</h1>
                <p className="mt-1 text-sm text-gray-500">
                  Actionable intelligence and recommendations for project optimization
                </p>
              </div>
              <div className="flex items-center space-x-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={showBaselineComparison}
                    onChange={(e) => setShowBaselineComparison(e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Show Baseline Comparison</span>
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Project Health Overview */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-medium text-gray-900">Project Health Overview</h2>
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800 border border-yellow-200">
              🟡 Moderate Risk
            </span>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {metrics.length > 0 ? (
              metrics.map((metric) => (
                <div key={metric.name} className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-medium text-gray-700">{metric.name}</h3>
                    {getTrendIcon(metric.trend)}
                  </div>
                  <div className="flex items-baseline space-x-2">
                    <span className={`text-2xl font-bold ${getMetricStatusColor(metric.status)}`}>
                      {metric.current}{metric.unit}
                    </span>
                    {showBaselineComparison && (
                      <span className="text-sm text-gray-500">
                        vs {metric.baseline}{metric.unit}
                      </span>
                    )}
                  </div>
                  {showBaselineComparison && (
                    <div className="mt-2">
                      <div className="flex justify-between text-xs text-gray-500 mb-1">
                        <span>Target: {metric.target}{metric.unit}</span>
                        <span>Baseline: {metric.baseline}{metric.unit}</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${(metric.current / metric.target) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="col-span-4 text-center py-8 text-gray-500">
                <ChartBarIcon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>No metrics data available</p>
              </div>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex items-center space-x-4">
            <span className="text-sm font-medium text-gray-700">Filter by Category:</span>
            {(['all', 'schedule', 'cost', 'resource', 'quality'] as const).map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                  selectedCategory === category
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category.charAt(0).toUpperCase() + category.slice(1)}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Critical Issues */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-red-50">
              <div className="flex items-center">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-600 mr-2" />
                <h3 className="text-lg font-medium text-red-900">
                  Critical Issues ({criticalIssues.length})
                </h3>
              </div>
            </div>
            <div className="divide-y divide-gray-200">
              {criticalIssues.length > 0 ? (
                criticalIssues.map((insight) => (
                  <div key={insight.id} className="p-6">
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="text-sm font-medium text-gray-900">{insight.title}</h4>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getSeverityColor(insight.severity)}`}>
                        {insight.severity}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-3">{insight.description}</p>
                    {showBaselineComparison && insight.baselineImpact && (
                      <div className="flex items-center text-xs text-gray-500 mb-2">
                        <span className="font-medium">Baseline Impact:</span>
                        <span className="ml-1">{insight.baselineImpact}</span>
                      </div>
                    )}
                    {insight.estimatedImpact && (
                      <div className="flex items-center text-xs text-red-600 mb-3">
                        <span className="font-medium">Estimated Impact:</span>
                        <span className="ml-1">{insight.estimatedImpact}</span>
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        {getTrendIcon(insight.trend)}
                        <span className="text-xs text-gray-500 capitalize">{insight.trend}</span>
                      </div>
                      {insight.actionRequired && (
                        <button className="text-xs text-blue-600 hover:text-blue-800 font-medium">
                          Take Action
                        </button>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-6 text-center text-gray-500">
                  No critical issues found
                </div>
              )}
            </div>
          </div>

          {/* Positive Indicators */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-green-50">
              <div className="flex items-center">
                <CheckCircleIcon className="h-5 w-5 text-green-600 mr-2" />
                <h3 className="text-lg font-medium text-green-900">
                  Positive Indicators ({positiveIndicators.length})
                </h3>
              </div>
            </div>
            <div className="divide-y divide-gray-200">
              {positiveIndicators.length > 0 ? (
                positiveIndicators.map((insight) => (
                  <div key={insight.id} className="p-6">
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="text-sm font-medium text-gray-900">{insight.title}</h4>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getSeverityColor(insight.severity)}`}>
                        {insight.severity}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-3">{insight.description}</p>
                    {showBaselineComparison && insight.baselineImpact && (
                      <div className="flex items-center text-xs text-gray-500 mb-2">
                        <span className="font-medium">Baseline Impact:</span>
                        <span className="ml-1">{insight.baselineImpact}</span>
                      </div>
                    )}
                    <div className="flex items-center space-x-2">
                      {getTrendIcon(insight.trend)}
                      <span className="text-xs text-gray-500 capitalize">{insight.trend}</span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-6 text-center text-gray-500">
                  No positive indicators found
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Recommendations */}
        <div className="mt-8 bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-blue-50">
            <div className="flex items-center">
              <LightBulbIcon className="h-5 w-5 text-blue-600 mr-2" />
              <h3 className="text-lg font-medium text-blue-900">
                Recommendations ({recommendations.length})
              </h3>
            </div>
          </div>
          <div className="divide-y divide-gray-200">
            {recommendations.length > 0 ? (
              recommendations.map((insight) => (
                <div key={insight.id} className="p-6">
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="text-sm font-medium text-gray-900">{insight.title}</h4>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getSeverityColor(insight.severity)}`}>
                      {insight.severity}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">{insight.description}</p>
                  {showBaselineComparison && insight.baselineImpact && (
                    <div className="flex items-center text-xs text-gray-500 mb-2">
                      <span className="font-medium">Baseline Impact:</span>
                      <span className="ml-1">{insight.baselineImpact}</span>
                    </div>
                  )}
                  {insight.estimatedImpact && (
                    <div className="flex items-center text-xs text-blue-600 mb-3">
                      <span className="font-medium">Estimated Impact:</span>
                      <span className="ml-1">{insight.estimatedImpact}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      {getTrendIcon(insight.trend)}
                      <span className="text-xs text-gray-500 capitalize">{insight.trend}</span>
                    </div>
                    {insight.actionRequired && (
                      <button className="text-xs text-blue-600 hover:text-blue-800 font-medium">
                        View Details
                      </button>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="p-6 text-center text-gray-500">
                No recommendations available
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectInsights; 

